import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Shield, Users, Newspaper, ScrollText, FileText, MessageSquareWarning, History, Menu, X, ExternalLink, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import { ROLE_LABEL } from "@/lib/format";

const ITEMS = [
  ["/dashboard", "Panoramica", LayoutDashboard, false, true],
  ["/dashboard/eventi", "Eventi e risultati", CalendarDays],
  ["/dashboard/clan", "Clan", Shield],
  ["/dashboard/candidature", "Candidature clan", UserPlus],
  ["/dashboard/news", "News", Newspaper],
  ["/dashboard/regolamenti", "Regolamenti", ScrollText],
  ["/dashboard/correzioni", "Richieste correzione", MessageSquareWarning],
  ["/dashboard/utenti", "Utenti e ruoli", Users, true],
  ["/dashboard/contenuti", "Contenuti alleanza", FileText, true],
  ["/dashboard/registro", "Registro attività", History, true],
];

function Nav({ onClick }) {
  const { user } = useAuth();
  return (
    <nav className="space-y-1" aria-label="Dashboard">
      {ITEMS.filter(([, , , sup]) => !sup || user.role === "super_admin").map(([to, label, Icon, , end]) => (
        <NavLink key={to} to={to} end={end} onClick={onClick} data-testid={`dash-nav-${to.split("/").pop()}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${isActive ? "bg-[#962FFF]/15 text-[#F5F5F7] border border-[#962FFF]/30" : "text-[#C6C6CE]/70 hover:text-[#F5F5F7] hover:bg-[#17171D] border border-transparent"}`}>
          <Icon className="w-4 h-4" />{label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardLayout() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="dashboard-layout">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-[#C6C6CE]/10 bg-[#0B0B0F] p-5 sticky top-0 h-screen">
        <Link to="/" className="mb-8" data-testid="dash-home-link"><Logo /></Link>
        <Nav />
        <div className="mt-auto pt-6 border-t border-[#C6C6CE]/10 text-xs">
          <div className="text-[#F5F5F7]">{user.display_name}</div>
          <div className="text-[#962FFF] uppercase tracking-widest text-[10px] mt-1">{ROLE_LABEL[user.role]}</div>
          <Link to="/area" className="mt-3 inline-flex items-center gap-1 text-[#C6C6CE]/60 hover:text-[#F5F5F7]">Area personale <ExternalLink className="w-3 h-3" /></Link>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center justify-between px-5 h-14 border-b border-[#C6C6CE]/10 bg-[#0B0B0F] sticky top-0 z-40">
          <Link to="/"><Logo /></Link>
          <button data-testid="dash-mobile-toggle" aria-label="Menu dashboard" onClick={() => setOpen(!open)} className="p-2">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
        </div>
        {open && <div className="lg:hidden p-4 border-b border-[#C6C6CE]/10 bg-[#0B0B0F]"><Nav onClick={() => setOpen(false)} /></div>}
        <main className="p-5 md:p-8 max-w-[1400px]"><Outlet /></main>
      </div>
    </div>
  );
}

export function DashHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <div className="eyebrow mb-2">Dashboard</div>
        <h1 data-testid="dash-title" className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-[#F5F5F7]">{title}</h1>
        {subtitle && <p className="text-sm text-[#C6C6CE]/60 mt-1">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
